import { useTranslations } from '@vocab/react';
import {
  Box,
  Card,
  Columns,
  Column,
  Stack,
  ButtonIcon,
  IconEdit,
  Drawer,
  Text,
} from 'braid-design-system';
import { useState } from 'react';

import { LoadingItem } from 'src/components/LoadingItem/LoadingItem';
import { useAnalytics } from 'src/contexts/AnalyticsContext';
import { useIsPublicProfileEnabled } from 'src/modules/features/useIsPublicProfileEnabled';
import { useGetProfileVisibility2 } from 'src/modules/graphql/visibility/data';

import translations from './.vocab';
import { EditApproachabilitySettings } from './EditApproachabilitySettings/EditApproachabilitySettings';
import { EditProfileVisibilitySettings } from './EditProfileVisibilitySettings/EditProfileVisibilitySettings';
import { LegacyEditApproachabilitySettings } from './LegacyEditApproachabilitySettings/LegacyEditApproachabilitySettings';
import { LegacyEditProfileVisibilitySettings } from './LegacyEditProfileVisibilitySettings/LegacyEditProfileVisibilitySettings';

export const ProfileVisibility = () => {
  const { t } = useTranslations(translations);
  const { trackProfileCardOpened } = useAnalytics();

  const { profileVisibility2, error, loading } = useGetProfileVisibility2();

  const [drawerOpened, setDrawerOpened] = useState(false);

  const toggle = () => {
    if (drawerOpened === false) {
      trackProfileCardOpened({
        currentPageSubsection: 'employer site',
      });
    }
    setDrawerOpened((currentState) => !currentState);
  };

  const isPublicProfileEnabled = useIsPublicProfileEnabled();

  const cardTitleLabel = isPublicProfileEnabled
    ? t('Set visibility')
    : t('Searchable by employers');

  const drawerHeading = isPublicProfileEnabled
    ? t('Profile visibility')
    : t('Searchable by employers');

  const drawerDescription = isPublicProfileEnabled
    ? t('Control who can search your profile...')
    : t('Your profile visibility setting controls...');

  return (
    <Box data-testid="profile-visibility-card">
      <Card rounded>
        <Columns space="small">
          <Column>
            {loading || error ? (
              <Stack space="medium">
                <LoadingItem size="standard" width="small" />
                <LoadingItem size="small" width="small" />
              </Stack>
            ) : (
              <Stack space="medium">
                <Text weight="strong">{cardTitleLabel}</Text>
                <Text tone="secondary">
                  {profileVisibility2?.label || t('Visibility not set')}
                </Text>
              </Stack>
            )}
          </Column>
          <Column width="content">
            <ButtonIcon
              id="editProfileVisibilityButton"
              variant="transparent"
              icon={<IconEdit />}
              label={t('Edit visibility')}
              onClick={toggle}
            />
          </Column>
        </Columns>
      </Card>
      <Drawer
        id="profileVisibilityDrawer"
        title={drawerHeading}
        open={drawerOpened === true}
        onClose={toggle}
        width="small"
        description={<Text>{drawerDescription}</Text>}
      >
        {isPublicProfileEnabled ? (
          <Stack space="xlarge">
            <EditProfileVisibilitySettings />
            <EditApproachabilitySettings />
          </Stack>
        ) : (
          <Stack space="xlarge">
            <LegacyEditProfileVisibilitySettings />
            <LegacyEditApproachabilitySettings />
          </Stack>
        )}
      </Drawer>
    </Box>
  );
};
